import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { API } from "@/lib/api";

const SANDBOX = "allow-scripts allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox";

// Sandboxed (opaque-origin) preview: demo code can never reach the admin app, its tokens or its API.
const PreviewFrame = forwardRef(function PreviewFrame({ content, assets, path, selectMode = false, textPick = false, selectedId = null, onRoute, onSelect, onPickText, onDiagnostics, onCapture, onInlineEdit, className = "" }, ref) {
  const iframe = useRef(null);
  const ready = useRef(false);
  const latest = useRef({});
  latest.current = { content, assets, path, selectMode, textPick, selectedId, onRoute, onSelect, onPickText, onDiagnostics, onCapture, onInlineEdit };

  const send = (msg) => iframe.current?.contentWindow?.postMessage({ __adm: true, ...msg }, "*");
  useImperativeHandle(ref, () => ({
    send,
    navigate: (p) => send({ type: "navigate", path: p }),
    capture: () => send({ type: "capture" }),
    reload: () => { ready.current = false; if (iframe.current) iframe.current.src = `${API}/runtime/frame?r=${Date.now()}`; },
  }));

  useEffect(() => {
    const onMsg = (e) => {
      if (!iframe.current || e.source !== iframe.current.contentWindow) return;
      const m = e.data;
      if (!m || !m.__adm) return;
      const L = latest.current;
      if (m.type === "ready") {
        ready.current = true;
        send({ type: "load", content: L.content, assets: L.assets, path: L.path });
        send({ type: "selectMode", on: L.selectMode });
        if (L.textPick) send({ type: "textPick", on: true });
        if (L.selectedId) send({ type: "selected", id: L.selectedId });
      } else if (m.type === "route") L.onRoute?.(m);
      else if (m.type === "select") L.onSelect?.(m);
      else if (m.type === "pickText") L.onPickText?.(m);
      else if (m.type === "diagnostics") L.onDiagnostics?.(m.items || []);
      else if (m.type === "capture") L.onCapture?.(m);
      else if (m.type === "parts") window.dispatchEvent(new CustomEvent("adm:parts", { detail: m }));
      else if (m.type === "inlineEdit" || m.type === "inlineEditUnavailable") L.onInlineEdit?.(m);
      else if (m.type === "key") window.dispatchEvent(new KeyboardEvent("keydown", { key: m.key, ctrlKey: true, shiftKey: !!m.shift }));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!ready.current || !content) return undefined;
    const t = setTimeout(() => send({ type: "load", content, assets }), 220);
    return () => clearTimeout(t);
  }, [content, assets]);
  useEffect(() => { if (ready.current) send({ type: "selectMode", on: selectMode }); }, [selectMode]);
  useEffect(() => { if (ready.current) send({ type: "textPick", on: textPick }); }, [textPick]);
  useEffect(() => { if (ready.current) send({ type: "selected", id: selectedId, scroll: true }); }, [selectedId]);

  return <iframe ref={iframe} title="Anteprima demo" src={`${API}/runtime/frame`} sandbox={SANDBOX} className={`w-full h-full border-0 bg-white ${className}`} data-testid="preview-iframe" />;
});

export default PreviewFrame;
